import { useForm, UseFormReturn, useWatch } from 'react-hook-form';
import {
    RequestMethod,
    RequestStatus,
    ServerRequestState,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import {
    OperatorLoginData,
    OperatorLoginRole,
} from '../../common/state/OperatorLoginData.ts';
import { Outlet, useOutletContext, useParams } from 'react-router-dom';
import { OperatorAsyncLoadedSection } from '../../app/components/OperatorAsyncLoadedSection.tsx';
import {
    Localized,
    useLanguage,
    useTranslation,
} from 'dg-web-shared/lib/Localization.tsx';
import { HeaderComponent } from '../../layout/components/HeaderComponent.tsx';
import { HeaderCancelSaveButtons } from '../../layout/components/HeaderCancelSaveButtons.tsx';
import { OperatorRoutedModalContent } from '../../layout/components/OperatorRoutedModalContent.tsx';
import {
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../../layout/components/form/FormLayout.tsx';
import { mandatoryFieldErrorMessage } from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import { AppBar, Tab, Tabs } from '@mui/material';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../../ui/modal/OperatorRoutedModal.tsx';
import { HeaderActionCloseButtons } from '../../layout/components/HeaderActionCloseButtons.tsx';
import { useOperatorContext } from '../../app/components/BaseLayoutAndData.tsx';
import { JSX, useEffect } from 'react';
import { Permissions } from '../../common/state/OperatorLoginsState.ts';
import { OtpOutlet } from './ConfirmOtpModal.tsx';
import {
    OperatorParking,
    useParkingsFetch,
} from '../../common/state/useParkingsFetch.ts';
import {
    ProductType,
    useOperatorLoginListOutlet,
} from './OperatorLoginsList.tsx';
import {
    RecodeCondition,
    useCloudConnectorRecodeConditions,
} from '../../cloud-connector/CloudConnectorRecodeQrCodesAdd.tsx';
import Alert from '@mui/material/Alert';
import {
    ApiErrorInterface,
    OperatorLoginCloudConnectorModule,
    OperatorLoginDetailFormFields,
    OperatorLoginDetailFormValues,
    OperatorLoginDetailParkingportal,
    OperatorLoginDetailSecurityContent,
    OperatorLoginDetailUserContent,
    OperatorLoginParkingaboModule,
    OperatorLoginParkingCheckModule,
    OperatorLoginParkingpayModule,
    operatorLoginPermissionFieldName,
    OperatorLoginSuperUserAccessRights,
    OperatorLoginTomEcoModule,
    OperatorLoginZoneSelect,
} from './OperatorLoginDetailForm.tsx';
import { Person, Work, WorkOutlined } from '@mui/icons-material';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';

interface UpdateOperatorLoginPayload {
    organizationLoginId: string;
    operatorLoginId: number | null;
    username: string;
    firstname: string;
    lastname: string;
    description: string | null;
    email: string;
    telnr: string | null;
    sendPasswordLink: boolean;
    allowedPermitTypes: number[];
    allowedProductTypes: number[];
    allowedZones: number[];
    allowedRecodeConditions: string[];
    permissions: Permissions;
    softAnonymizeCheckinPublicPermitAfterDays: number | null;
}

export function useOperatorLoginDetailOutletContext() {
    return useOutletContext<OperarotorLoginDetailContext>();
}

function OperatorLoginDetailOutlet({
    context,
}: {
    context: OperarotorLoginDetailContext;
}) {
    return <Outlet context={context} />;
}

export interface OperarotorLoginDetailContext {
    loginData: OperatorLoginData;
    refetchOperatorLoginData: () => void;
    onDirtyStateChange: (shouldConfirm: boolean) => void;
    onClose: () => void;
    navigateWithinModal: (url: string) => void;
    refetchOperatorLoginList: () => void;
}

export function OperatorLoginDetailContext() {
    const { organizationLoginId } = useParams<{
        organizationLoginId: string;
    }>();
    const { refetchOperatorLoginList } = useOperatorLoginListOutlet();
    const [operatorLoginDataState, refetchOperatorLoginData] = useServerFetch<
        OperatorLoginData,
        { organizationLoginId: string }
    >(
        ({ organizationLoginId }) => ({
            url: `/ui-api/operator-account/operator-login/${organizationLoginId}/detail`,
        }),
        organizationLoginId
            ? { organizationLoginId: organizationLoginId }
            : null,
    );
    return (
        <OperatorRoutedModal
            variant={ModalVariant.BIG}
            backUrl={'..'}
            render={controller => (
                <OperatorAsyncLoadedSection
                    requestState={operatorLoginDataState}
                    render={loginData => (
                        <OperatorLoginDetailOutlet
                            context={{
                                loginData,
                                refetchOperatorLoginData,
                                onDirtyStateChange:
                                    controller.setShouldConfirmBeforeLeave,
                                onClose: controller.close,
                                navigateWithinModal:
                                    controller.navigateWithinModal,
                                refetchOperatorLoginList,
                            }}
                        />
                    )}
                />
            )}
        />
    );
}

export function OperatorLoginDetail({
    operatorLoginDetailTab,
}: {
    operatorLoginDetailTab: OperatorLoginDetailTab;
}) {
    const {
        loginData,
        refetchOperatorLoginData,
        onClose,
        onDirtyStateChange,
        refetchOperatorLoginList,
    } = useOperatorLoginDetailOutletContext();
    const form = useForm<OperatorLoginDetailFormValues>({
        defaultValues: {
            [OperatorLoginDetailFormFields.username]: loginData.username,
            [OperatorLoginDetailFormFields.firstname]: loginData.firstname,
            [OperatorLoginDetailFormFields.lastname]: loginData.lastname,
            [OperatorLoginDetailFormFields.description]: loginData.description,
            [OperatorLoginDetailFormFields.email]: loginData.email,
            [OperatorLoginDetailFormFields.telnr]: loginData.telnr,
            [OperatorLoginDetailFormFields.sendPasswordLink]: false,
            [OperatorLoginDetailFormFields.allowedPermitTypes]:
                loginData.allowedPermitTypes,
            [OperatorLoginDetailFormFields.allowedProductTypes]:
                loginData.allowedProductTypes,
            [OperatorLoginDetailFormFields.allowedZones]:
                loginData.allowedZones,
            [OperatorLoginDetailFormFields.allowedRecodeConditions]:
                loginData.allowedRecodeConditions,
            [OperatorLoginDetailFormFields.allowedParkingaboRoles]:
                loginData.allowedParkingaboRoles,
            [OperatorLoginDetailFormFields.permissions]: loginData.permissions,
            [OperatorLoginDetailFormFields.softAnonymizeCheckinPublicPermitAfterDays]:
                loginData.softAnonymizeCheckinPublicPermitAfterDays,
        },
        mode: 'onBlur',
    });
    const isDirty = form.formState.isDirty;
    useEffect(() => {
        onDirtyStateChange(isDirty);
    }, [isDirty, onDirtyStateChange]);
    const formReset = form.reset;
    useEffect(formReset, [operatorLoginDetailTab, formReset]);
    const { language } = useLanguage();
    const [
        updateOperatorLoginState,
        updateOperatorLogin,
        resetUpdateOperatorLoginRequest,
    ] = useServerWrite<
        UpdateOperatorLoginPayload,
        null,
        ApiErrorInterface | null
    >(() => ({
        url: `/ui-api/operator-account/operator-logins/${loginData.organizationLoginId}?language=${language}`,
        method: RequestMethod.PUT,
    }));
    useServerSuccessEffect(updateOperatorLoginState, () => {
        refetchOperatorLoginData();
        refetchOperatorLoginList();
    });

    const [parkingState] = useParkingsFetch();
    const [productTypeState] = useServerFetch<ProductType[], object>(
        () => ({
            url: `/ui-api/operator-account/product-templates`,
        }),
        {},
    );
    const [recodeConditionState] = useCloudConnectorRecodeConditions();
    const roleIconMap = {
        [OperatorLoginRole.ADMIN]: Work,
        [OperatorLoginRole.SUPER_USER]: WorkOutlined,
        [OperatorLoginRole.USER]: Person,
    };

    return (
        <OperatorRoutedModalContent
            header={
                <HeaderComponent
                    title={loginData.username}
                    Icon={roleIconMap[loginData.role]}
                    editMode={form.formState.isDirty}
                    buttons={
                        form.formState.isDirty ? (
                            <HeaderCancelSaveButtons
                                onSave={form.handleSubmit(data =>
                                    updateOperatorLogin({
                                        organizationLoginId:
                                            loginData.organizationLoginId,
                                        operatorLoginId:
                                            loginData.operatorLoginId,
                                        ...data,
                                    }),
                                )}
                                onCancel={() => {
                                    form.reset();
                                    resetUpdateOperatorLoginRequest();
                                }}
                                loading={
                                    updateOperatorLoginState.status ==
                                    RequestStatus.PENDING
                                }
                            />
                        ) : (
                            <HeaderActionCloseButtons onClose={onClose} />
                        )
                    }
                />
            }
            bar={
                <OperatorLoginDetailTabs
                    operatorLoginDetailTab={operatorLoginDetailTab}
                    hasDetail={
                        !loginData.permissions.userPermissionEdit &&
                        loginData.permissions.operatorAppActive
                    }
                />
            }
            body={
                <OperatorAsyncLoadedSection
                    requestState={parkingState}
                    render={parkings => (
                        <OperatorAsyncLoadedSection
                            requestState={productTypeState}
                            render={productTypes => (
                                <OperatorAsyncLoadedSection
                                    requestState={recodeConditionState}
                                    render={recodeConditions => (
                                        <>
                                            {loginData.deletionDate && (
                                                <OperatorLoginDeletionDateInfo
                                                    deletionDate={
                                                        loginData.deletionDate
                                                    }
                                                />
                                            )}
                                            <OperatorLoginDetailErrorWarning
                                                requestState={
                                                    updateOperatorLoginState
                                                }
                                            />
                                            <OperatorLoginDetailContent
                                                form={form}
                                                operatorLoginDetailTab={
                                                    operatorLoginDetailTab
                                                }
                                                parkings={parkings}
                                                productTypes={productTypes}
                                                recodeConditions={
                                                    recodeConditions
                                                }
                                            />
                                        </>
                                    )}
                                />
                            )}
                        />
                    )}
                />
            }
        />
    );
}

export enum OperatorLoginDetailTab {
    GENERAL = 'GENERAL',
    DETAIL = 'DETAIL',
}

export function OperatorLoginDetailTabs({
    operatorLoginDetailTab,
    hasDetail,
}: {
    operatorLoginDetailTab: OperatorLoginDetailTab;
    hasDetail: boolean;
}): JSX.Element {
    const { navigateWithinModal, loginData } =
        useOperatorLoginDetailOutletContext();
    return (
        <AppBar position="static" elevation={0}>
            <Tabs
                value={operatorLoginDetailTab}
                onChange={(_e, value) => {
                    switch (value as OperatorLoginDetailTab) {
                        case OperatorLoginDetailTab.GENERAL:
                            navigateWithinModal(
                                `../operator-login/${loginData.organizationLoginId}/general`,
                            );
                            break;
                        case OperatorLoginDetailTab.DETAIL:
                            navigateWithinModal(
                                `../operator-login/${loginData.organizationLoginId}/detail`,
                            );
                            break;
                    }
                }}
                indicatorColor="secondary"
                textColor="inherit"
            >
                <Tab
                    style={{
                        marginLeft: 24,
                    }}
                    label={
                        <Localized
                            de="Allgemein"
                            fr="Général"
                            it="Generale"
                            en="General"
                        />
                    }
                    value={OperatorLoginDetailTab.GENERAL}
                />
                {hasDetail && (
                    <Tab
                        label={
                            <Localized
                                de="Details"
                                fr="Détails"
                                it="Dettagli"
                                en="Details"
                            />
                        }
                        value={OperatorLoginDetailTab.DETAIL}
                    />
                )}
            </Tabs>
        </AppBar>
    );
}

export function OperatorLoginDetailErrorWarning({
    requestState,
}: {
    requestState: ServerRequestState<object | null, ApiErrorInterface | null>;
}) {
    if (requestState.status !== RequestStatus.ERROR) {
        return null;
    }
    if (requestState.data?.message === 'DUPLICATE_OPERATOR_LOGIN_USERNAME') {
        return (
            <Alert severity="error" sx={{ margin: '0 0 20px 0' }}>
                <Localized
                    de="Dieser Benutzername ist bereits belegt"
                    fr="Ce nom d'utilisateur est déjà utilisé"
                    it="Questo nome utente é già in uso"
                    en="This username is already taken"
                />
            </Alert>
        );
    }
    return (
        <Alert severity="error" sx={{ margin: '0 0 20px 0' }}>
            <Localized
                de="Es ist ein Fehler aufgetreten."
                fr="Une erreur est survenue."
                it="Si è verificato un errore."
                en="An error has occurred."
            />
        </Alert>
    );
}

export function OperatorLoginDeletionDateInfo({
    deletionDate,
}: {
    deletionDate: string;
}) {
    const dateFormatted = Formatter.dayMonthYear(
        DateTime.fromISO(deletionDate),
    );
    return (
        <Alert severity="info" sx={{ margin: '0 0 20px 0' }}>
            <Localized
                de={`Sollte dieser Benutzer weiterhin inaktiv bleiben, wird dieser automatisch am ${dateFormatted} gelöscht.`}
                fr={`Si cet utilisateur reste inactif, il sera automatiquement supprimé le ${dateFormatted}.`}
                it={`Se questo utente dovesse rimanere inattivo, verrà automaticamente cancellato il ${dateFormatted}.`}
                en={`If this user remains inactive, their account will be automatically deleted on ${dateFormatted}.`}
            />
        </Alert>
    );
}

function OperatorLoginDetailContent({
    form,
    operatorLoginDetailTab,
    parkings,
    productTypes,
    recodeConditions,
}: {
    form: UseFormReturn<OperatorLoginDetailFormValues>;
    operatorLoginDetailTab: OperatorLoginDetailTab;
    parkings: OperatorParking[];
    productTypes: ProductType[];
    recodeConditions: RecodeCondition[];
}) {
    const { loginData, refetchOperatorLoginData } =
        useOperatorLoginDetailOutletContext();
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);
    const { operatorData, currentLogin } = useOperatorContext();
    const superUserPermissions = useWatch({
        control: form.control,
        name: operatorLoginPermissionFieldName('userPermissionEdit'),
    });
    const email = useWatch({
        control: form.control,
        name: OperatorLoginDetailFormFields.email,
    });
    const isNewEmail = loginData.email !== email;
    const readonly = !currentLogin.isAdmin && superUserPermissions;

    if (loginData.organizationLoginId === currentLogin.organizationLoginId) {
        return (
            <Alert severity="warning" sx={{ m: 0 }}>
                <Localized
                    de="Eigener Benutzer kann nicht bearbeitet werden"
                    fr="Un utilisateur ne peut pas se modifier lui-même"
                    it="Un utente non può modificare se stesso"
                    en="User cannot edit themselves"
                />
            </Alert>
        );
    }

    switch (operatorLoginDetailTab) {
        case OperatorLoginDetailTab.GENERAL:
            return (
                <FormContentLayout>
                    <FormLeftColumn>
                        <OperatorLoginDetailUserContent
                            form={form}
                            readonly={readonly}
                            mandatoryFieldMessage={mandatoryFieldMessage}
                            isAdmin={currentLogin.isAdmin}
                            maxSoftAnonymizeCheckinPublicPermitAfterDays={
                                loginData.maxSoftAnonymizeCheckinPublicPermitAfterDays
                            }
                            isCreate={false}
                            emailSuppression={loginData.emailSuppression}
                        />
                        <OperatorLoginDetailSecurityContent
                            form={form}
                            readonly={readonly || isNewEmail}
                            isDirty={form.formState.isDirty}
                            isCreate={false}
                            otpEnabled={loginData.otpEnabled}
                            operatorLoginId={loginData.operatorLoginId}
                        />
                    </FormLeftColumn>
                    <FormRightColumn>
                        {superUserPermissions ? (
                            <OperatorLoginSuperUserAccessRights
                                form={form}
                                readonly={readonly}
                            />
                        ) : (
                            <>
                                <OperatorLoginZoneSelect
                                    form={form}
                                    readonly={readonly}
                                    parkings={parkings}
                                />
                                <OperatorLoginDetailParkingportal
                                    form={form}
                                    readonly={readonly}
                                    operatorData={operatorData}
                                    maxSoftAnonymizeCheckinPublicPermitAfterDays={
                                        loginData.maxSoftAnonymizeCheckinPublicPermitAfterDays
                                    }
                                />
                                <OperatorLoginParkingCheckModule
                                    form={form}
                                    readonly={readonly}
                                    operatorData={operatorData}
                                />
                            </>
                        )}
                    </FormRightColumn>
                    <OtpOutlet
                        context={{
                            refetchAfterSuccess: refetchOperatorLoginData,
                        }}
                    />
                </FormContentLayout>
            );
        case OperatorLoginDetailTab.DETAIL:
            return (
                <FormContentLayout>
                    <FormLeftColumn>
                        <OperatorLoginParkingpayModule
                            form={form}
                            readonly={readonly}
                            operatorData={operatorData}
                            productTypes={productTypes}
                        />
                        {operatorData.numberOfActiveParkingaboRoles > 0 && (
                            <OperatorLoginParkingaboModule
                                form={form}
                                readonly={readonly}
                            />
                        )}
                    </FormLeftColumn>
                    <FormRightColumn>
                        {operatorData.hasCloudConnector && (
                            <OperatorLoginCloudConnectorModule
                                form={form}
                                readonly={readonly}
                                recodeConditions={recodeConditions}
                            />
                        )}
                        {operatorData.hasTaxomexModule && (
                            <OperatorLoginTomEcoModule
                                form={form}
                                readonly={readonly}
                                operatorData={operatorData}
                            />
                        )}
                    </FormRightColumn>
                </FormContentLayout>
            );
    }
}
